import React, { useState } from 'react';
import { cn } from '@design-system';
import { isNullUndefinedOrEmpty, publicAssetUrl } from '@shared';
import { WaterlilyLogo } from './WaterlilyLogo';

interface CompanyLogoContainerProps {
  organizationDisplayName: string | null | undefined;
  advisorLogoKey?: string | null;
  brandingPreference?: WhitelabelBrandingPreference;
  waterlilyFallback?: 'text' | 'logo';
  containerClassName?: string;
  organizationNameClassName?: string;
  subtextClassName?: string;
  onClick?: () => void;
}

function formatOrgDisplayName(input: string): string {
  return input.replace(/\b[a-z]/g, (char, index, sentence) => {
    // Check if the previous character is an apostrophe
    if (index > 0 && sentence[index - 1] === "'") {
      return char;
    }
    return char.toUpperCase();
  });
}

const CompanyLogoContainer: React.FC<CompanyLogoContainerProps> = ({
  organizationDisplayName,
  advisorLogoKey,
  brandingPreference,
  waterlilyFallback = 'text',
  containerClassName,
  organizationNameClassName,
  subtextClassName,
  onClick,
}) => {
  const [logoFailed, setLogoFailed] = useState(false);

  // The preference picks the branding asset; if that asset is missing (or fails to load),
  // fall back to plain Waterlily branding.
  const showName =
    brandingPreference === 'name' && !isNullUndefinedOrEmpty(organizationDisplayName);
  const showLogo =
    brandingPreference === 'logo' && !isNullUndefinedOrEmpty(advisorLogoKey) && !logoFailed;

  const waterlilyBranding =
    waterlilyFallback === 'logo' ? (
      <WaterlilyLogo className="h-10 w-auto" />
    ) : (
      <div className="mt-2 text-2xl font-semibold text-gray-950 md:text-3xl">Waterlily</div>
    );

  return (
    <div
      className={cn(containerClassName, 'mr-5 flex min-w-max flex-col items-start', {
        'cursor-pointer': onClick,
      })}
      id="white-label-container"
      onClick={onClick}
    >
      {showLogo || showName ? (
        <>
          {/** Do not remove whitelabel-logo id, it is used for pdf report generation */}
          <div
            id="whitelabel-logo"
            className={cn(
              'max-w-56 leading-tight! sm:max-w-max',
              {
                'text-primary-dark text-sm font-semibold md:text-base': !organizationNameClassName,
              },
              organizationNameClassName,
            )}
          >
            {showLogo ? (
              <img
                src={publicAssetUrl(advisorLogoKey!)}
                alt={organizationDisplayName || 'Organization logo'}
                crossOrigin="anonymous"
                onError={() => setLogoFailed(true)}
                className="h-10 w-auto max-w-56 object-contain"
              />
            ) : (
              formatOrgDisplayName(organizationDisplayName!)
            )}
          </div>
          {/* Only the name lockup carries the attribution: logos vary in style, color, and
              aspect ratio, so a fixed caption cannot pair predictably with them */}
          {showName && (
            <div
              className={subtextClassName ? subtextClassName : 'text-foreground text-xs md:text-sm'}
            >
              powered by Waterlily
            </div>
          )}
        </>
      ) : (
        waterlilyBranding
      )}
    </div>
  );
};

export default CompanyLogoContainer;
